import { motion, AnimatePresence } from 'framer-motion'
import { X } from 'lucide-react'
import { useTranslation } from 'react-i18next'

interface PrivacyModalProps {
  isOpen: boolean
  onClose: () => void
}

export function PrivacyModal({ isOpen, onClose }: PrivacyModalProps) {
  const { t } = useTranslation()

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/50 z-[100]"
          />

          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            className="fixed inset-0 z-[100] flex items-center justify-center p-4 pb-20 md:pb-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="bg-white rounded-2xl shadow-xl max-w-2xl w-full max-h-[calc(90vh-5rem)] md:max-h-[90vh] overflow-hidden flex flex-col">
              <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
                <h2 className="text-xl font-bold text-text-primary">
                  {t('auth.privacyPolicy')}
                </h2>
                <button
                  onClick={onClose}
                  className="p-2 rounded-full hover:bg-gray-100 transition-colors"
                >
                  <X className="h-5 w-5 text-text-secondary" />
                </button>
              </div>

              <div className="overflow-y-auto px-6 py-4 flex-1">
                <div className="prose prose-sm max-w-none text-text-primary space-y-4">
                  <div>
                    <h3 className="text-lg font-semibold mb-2">
                      {t('privacy.lastUpdated', 'Última atualização')}: {new Date().toLocaleDateString()}
                    </h3>
                    
                    <section className="mb-6">
                      <h4 className="font-semibold mb-2">{t('privacy.introduction', '1. Introdução')}</h4>
                      <p className="text-text-secondary text-sm leading-relaxed">
                        {t('privacy.introductionText', 'O Djelo Wallet respeita a sua privacidade e está comprometido em proteger os seus dados pessoais. Esta Política de Privacidade explica como recolhemos, utilizamos e protegemos as suas informações.')}
                      </p>
                    </section>

                    <section className="mb-6">
                      <h4 className="font-semibold mb-2">{t('privacy.dataCollection', '2. Dados que Recolhemos')}</h4>
                      <p className="text-text-secondary text-sm leading-relaxed">
                        {t('privacy.dataCollectionText', 'Recolhemos informações que nos fornece diretamente, incluindo nome, endereço de e-mail, número de telefone, data de nascimento, nacionalidade e informações de pagamento. Também recolhemos dados sobre o uso do serviço e informações do dispositivo.')}
                      </p>
                    </section>

                    <section className="mb-6">
                      <h4 className="font-semibold mb-2">{t('privacy.dataUsage', '3. Como Utilizamos os Seus Dados')}</h4>
                      <p className="text-text-secondary text-sm leading-relaxed">
                        {t('privacy.dataUsageText', 'Utilizamos os seus dados para fornecer e melhorar o nosso serviço, processar transações, verificar a sua identidade, comunicar consigo e cumprir obrigações legais. Não vendemos os seus dados pessoais a terceiros.')}
                      </p>
                    </section>

                    <section className="mb-6">
                      <h4 className="font-semibold mb-2">{t('privacy.dataSharing', '4. Partilha de Dados')}</h4>
                      <p className="text-text-secondary text-sm leading-relaxed">
                        {t('privacy.dataSharingText', 'Podemos partilhar os seus dados apenas com prestadores de serviços confiáveis que nos ajudam a operar o nosso serviço, com autoridades legais quando exigido por lei, ou com o seu consentimento explícito.')}
                      </p>
                    </section>

                    <section className="mb-6">
                      <h4 className="font-semibold mb-2">{t('privacy.dataSecurity', '5. Segurança dos Dados')}</h4>
                      <p className="text-text-secondary text-sm leading-relaxed">
                        {t('privacy.dataSecurityText', 'Implementamos medidas de segurança técnicas e organizacionais adequadas para proteger os seus dados contra acesso não autorizado, alteração, divulgação ou destruição. Utilizamos criptografia e outras tecnologias de segurança.')}
                      </p>
                    </section>

                    <section className="mb-6">
                      <h4 className="font-semibold mb-2">{t('privacy.yourRights', '6. Os Seus Direitos')}</h4>
                      <p className="text-text-secondary text-sm leading-relaxed">
                        {t('privacy.yourRightsText', 'Tem o direito de aceder, retificar, apagar ou restringir o processamento dos seus dados pessoais. Também pode opor-se ao processamento ou solicitar a portabilidade dos seus dados. Para exercer estes direitos, contacte-nos.')}
                      </p>
                    </section>

                    <section className="mb-6">
                      <h4 className="font-semibold mb-2">{t('privacy.cookies', '7. Cookies e Tecnologias Similares')}</h4>
                      <p className="text-text-secondary text-sm leading-relaxed">
                        {t('privacy.cookiesText', 'Utilizamos cookies e tecnologias similares para melhorar a sua experiência, analisar o uso do serviço e personalizar o conteúdo. Pode gerir as preferências de cookies nas definições do seu navegador.')}
                      </p>
                    </section>

                    <section className="mb-6">
                      <h4 className="font-semibold mb-2">{t('privacy.changes', '8. Alterações a Esta Política')}</h4>
                      <p className="text-text-secondary text-sm leading-relaxed">
                        {t('privacy.changesText', 'Podemos atualizar esta Política de Privacidade periodicamente. Notificaremos sobre alterações significativas através do nosso serviço ou por e-mail. Recomendamos que revise esta política regularmente.')}
                      </p>
                    </section>

                    <section className="mb-6">
                      <h4 className="font-semibold mb-2">{t('privacy.contact', '9. Contacte-nos')}</h4>
                      <p className="text-text-secondary text-sm leading-relaxed">
                        {t('privacy.contactText', 'Se tiver questões sobre esta Política de Privacidade ou sobre o processamento dos seus dados pessoais, contacte-nos através dos canais disponíveis na aplicação.')}
                      </p>
                    </section>
                  </div>
                </div>
              </div>

              <div className="sticky bottom-0 bg-white border-t border-gray-200 px-6 py-4">
                <button
                  onClick={onClose}
                  className="w-full bg-primary text-white rounded-lg py-2 px-4 hover:bg-primary/90 transition-colors"
                >
                  {t('common.close')}
                </button>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  )
}

